/**
 * Relay and lane-ring states (M3 spec §6.3, §9; plan R5). An absent relay entry means the generator's `online` flag
 * with no deliveries; an absent lane-ring entry means `LaneRingSite.initiallyUp`. Writers create entries; readers
 * never do.
 */
import type { LaneRingSite } from '../../galaxy/layout/types'
import type { Galaxy } from '../../galaxy/types'
import type { PartId, ReachState, RelayState, RelayStatus } from '../types'
import { galaxyIndex } from './galaxyIndex'

function defaultRelayState(online: boolean): RelayState {
  return { status: online ? 'online' : 'offline', delivered: {}, attempts: 0, scanned: false, sponsor: null, sponsorPending: false, relinkedAt: null }
}

function generatorOnline(galaxy: Galaxy, relayId: string): boolean {
  const relay = galaxyIndex(galaxy).relays.get(relayId)
  if (!relay) throw new Error(`Unknown relay "${relayId}"`)
  return relay.online
}

/**
 * The relay's state. When nothing is stored this is a fresh default (from the generator's `online` flag) that is
 * NOT stored: mutate through `ensureRelayState` or the writers below. Throws on an id the galaxy does not know.
 */
export function relayStateOf(state: ReachState, galaxy: Galaxy, relayId: string): Readonly<RelayState> {
  return state.relays[relayId] ?? defaultRelayState(generatorOnline(galaxy, relayId))
}

/** The stored relay state, created from the default on first use. */
export function ensureRelayState(state: ReachState, galaxy: Galaxy, relayId: string): RelayState {
  let rs = state.relays[relayId]
  if (!rs) {
    rs = defaultRelayState(generatorOnline(galaxy, relayId))
    state.relays[relayId] = rs
  }
  return rs
}

/** Allocation-free: online (not offline, not mid-reboot). Throws on an unknown id. */
export function isRelayOnline(state: ReachState, galaxy: Galaxy, relayId: string): boolean {
  const rs = state.relays[relayId]
  return rs ? rs.status === 'online' : generatorOnline(galaxy, relayId)
}

/** True when the status changed. */
export function setRelayStatus(state: ReachState, galaxy: Galaxy, relayId: string, status: RelayStatus): boolean {
  const rs = ensureRelayState(state, galaxy, relayId)
  if (rs.status === status) return false
  rs.status = status
  return true
}

/** Adds `qty` (a positive whole number; anything else adds nothing) of `part`. Returns the new delivered total. */
export function addDelivered(state: ReachState, galaxy: Galaxy, relayId: string, part: PartId, qty: number): number {
  const rs = ensureRelayState(state, galaxy, relayId)
  const add = Number.isFinite(qty) ? Math.max(0, Math.floor(qty)) : 0
  const total = (rs.delivered[part] ?? 0) + add
  if (add > 0) rs.delivered[part] = total
  return total
}

/** Clears every delivered part (a destroyed relay loses them). True when anything was cleared. */
export function clearDelivered(state: ReachState, galaxy: Galaxy, relayId: string): boolean {
  const rs = state.relays[relayId]
  if (!rs) {
    generatorOnline(galaxy, relayId) // validates the id
    return false
  }
  if (Object.keys(rs.delivered).length === 0) return false
  rs.delivered = {}
  return true
}

/** Allocation-free: whether the lane ring is up. */
export function laneRingUp(state: ReachState, ring: LaneRingSite): boolean {
  return state.laneRings[ring.id] ?? ring.initiallyUp
}

/** True when the ring's state changed. Only differences from `initiallyUp` are stored. */
export function setLaneRingUp(state: ReachState, ring: LaneRingSite, up: boolean): boolean {
  const changed = laneRingUp(state, ring) !== up
  if (up === ring.initiallyUp) Reflect.deleteProperty(state.laneRings, ring.id)
  else state.laneRings[ring.id] = up
  return changed
}
